import { LegalPage } from "../../components/LegalPage";

/**
 * S44 — Trust hub methodology page for the Narcotic Destruction
 * documentation feature.
 *
 * This page is part of the Trust hub and documents WeConsult Rx's
 * implementation approach + limitations + state coverage. It does NOT
 * provide legal advice; users must verify state-specific requirements
 * with their compliance officer before production use.
 */
export function NarcoticDestructionMethodologyPage() {
  return (
    <LegalPage
      title="Narcotic destruction compliance methodology"
      eyebrow="Trust center"
      description="How WeConsult Rx supports DEA + state-board controlled-substance destruction documentation: 21 CFR 1317, 21 CFR 1304, 21 CFR 1311, 15 USC §7001 ESIGN Act, UETA, and per-state rule layering."
      path="/trust/narcotic-destruction-compliance"
      effective="2026-05-31"
    >
      <p>
        WeConsult Rx provides a multi-facility-type documentation surface for
        the destruction of controlled substances. The feature is designed to
        produce a signed, cryptographically-sealed record that satisfies the
        federal records-and-reports floor and overlays state-board
        requirements when applicable.
      </p>

      <h2>Federal framework</h2>
      <ul>
        <li>
          <strong>21 CFR Part 1317</strong> — DEA disposal of controlled
          substances (restructured 2014; allows reverse distributors, on-site
          witness destruction, mail-back, and collection receptacles).
        </li>
        <li>
          <strong>21 CFR Part 1304</strong> — Records and reports required of
          DEA registrants.
        </li>
        <li>
          <strong>21 CFR Part 1311</strong> — Electronic prescribing of
          controlled substances; used here as the analog framework for
          electronic-record + electronic-signature acceptability.
        </li>
        <li>
          <strong>DEA Form 41</strong> — Registrants Inventory of Drugs
          Surrendered; required when surrendering to a DEA-authorized
          collector or the local DEA office.
        </li>
        <li>
          <strong>21 USC 822(g)</strong> — Long-term care facility exemption
          allowing facilities to dispose of residents' controlled substances
          under specific conditions.
        </li>
        <li>
          <strong>15 USC §7001 (ESIGN Act) + UETA</strong> — Electronic
          signatures and records carry the same legal weight as wet
          signatures provided the signer's identity is uniquely bound to the
          record and subsequent changes invalidate the binding.
        </li>
      </ul>

      <h2>Per-facility-type method coverage</h2>
      <ul>
        <li>
          <strong>SNF (skilled nursing)</strong> — reverse distributor,
          on-site witness destruction with two-witness signature requirement,
          bedside wastage at administration, DEA Form 41 surrender.
        </li>
        <li>
          <strong>ASC (ambulatory surgical center)</strong> — reverse
          distributor, on-site witness destruction, DEA Form 41 surrender;
          anesthesia-cart waste typically requires CRNA + RN co-signature.
        </li>
        <li>
          <strong>Dialysis</strong> — reverse distributor, on-site witness
          destruction, bedside wastage; bedside wastage covers fentanyl /
          alfentanil for procedural sedation.
        </li>
        <li>
          <strong>ALF (assisted living)</strong> — typically a non-DEA
          registrant; reverse distributor, mail-back program, and on-site
          collection receptacle are the supported pathways.
        </li>
        <li>
          <strong>Pharmacy (consultant/community)</strong> — reverse
          distributor, on-site witness destruction, DEA Form 41 surrender,
          mail-back program for patient-returned medications.
        </li>
      </ul>

      <h2>Per-state rule overlay</h2>
      <p>
        Each event captures the facility's state code at creation time. The
        rule engine looks up a per-(state, facility_type) row that specifies
        allowed methods, required witness count, acceptable witness
        credentials, record retention, and any conditional DEA Form 41
        triggers.
      </p>
      <p>
        As of June 2026, reference rules are seeded for{" "}
        <strong>all 50 states + the District of Columbia + Puerto Rico</strong>.
        Each seed row carries a citation URL to the underlying state board /
        agency publication and a <code>last_updated</code> date (per-state
        sources include the relevant state pharmacy board publication, the DEA
        Diversion Control state board lookup, and the NABP state board
        directory).
      </p>
      <p>
        FL, TX, CA, NY, OH, PA, IL, GA, NC, MI carry the most-granular
        per-facility-type rows (SNF, ASC, dialysis, pharmacy, ALF). The
        remaining states carry a state-wide <code>any</code> row that maps to
        the federal floor under 21 CFR 1317 with the state pharmacy board
        citation. AZ, CO, CT, MA, MD, MN, NJ, VA, WA additionally carry SNF /
        ASC / pharmacy facility-specific overlays.
      </p>
      <p>
        For state rows where the citation points to the state pharmacy board's
        general landing page rather than an explicit destruction-procedure
        rule, the row is intentionally conservative. It applies a conservative
        default retention (5 years unless the state regulator publishes a
        shorter or longer window we can directly cite), the federal-floor
        witness count of two, and the federal-floor allowed methods
        (reverse distributor, on-site witness destruction, DEA Form 41
        surrender). The <code>notes</code> field flags the row as requiring
        counsel review before production use; the value in
        <code>record_retention_years</code> on a federal-floor row should
        not be read as a direct quotation from a state publication.
      </p>
      <p>
        Every row — regardless of facility_type granularity — carries the
        marker <code>requires-compliance-counsel-review-before-production-use</code>{" "}
        in the <code>notes</code> column. This is reference content, not a
        regulatory determination.
      </p>

      <h2>Signature methodology</h2>
      <ul>
        <li>
          <strong>Canvas pad</strong> — captures a PNG signature via
          <code>react-signature-canvas</code> in the PWA. Stored as a base64
          data URL associated with the witness row. Used for nurse / DON /
          pharmacist signoff on bedside or on-site destruction.
        </li>
        <li>
          <strong>Typed attestation</strong> — text input renders a cursive
          SVG to preserve a visual signature in the sealed PDF when the
          witness is signing remotely or in a headless context.
        </li>
        <li>
          <strong>WebAuthn passkey</strong> — supports platform authenticators
          (Touch ID / Windows Hello / Face ID) that sign an Ed25519
          attestation bound to the event hash. Roadmap.
        </li>
        <li>
          <strong>Ed25519 server-issued key</strong> — for high-assurance
          flows where the server generates an ephemeral keypair signed against
          the event hash and surfaces the public key + signature to the
          witness row.
        </li>
        <li>
          <strong>Wet-signature uploaded image</strong> — supports hybrid
          paper / digital workflows by capturing an image upload of a wet
          signature.
        </li>
      </ul>

      <h2>Cryptographic seal</h2>
      <p>
        At first witness signing, the server computes a SHA-256 hash of the
        canonical-sorted JSON of <code>{`{event_id, facility_id,
        destruction_method, destruction_date, items: [...]}`}</code>. All
        subsequent witnesses sign the SAME hash — proving they agreed on the
        same data. Any later item mutation produces a different hash, the
        binding is invalidated, and the witness submission is rejected.
      </p>
      <p>
        Finalization renders a PDF embedding the event_hash, citation footer,
        and a QR code linking to the public verification endpoint
        <code>/v1/narcotic-destruction/verify/[hash]</code>. Auditors scanning
        the QR code see only <code>found</code>, <code>finalized_at</code>,
        and <code>witness_count</code> — no PHI is exposed.
      </p>

      <h2>Record retention</h2>
      <p>
        Per-state retention windows are stored in the rule engine and range
        from 2 to 7 years across the seeded states. The federal floor under
        21 CFR 1304 is two years; states that exceed it (CA = 7y, FL/TX/NY =
        5y) drive the retention window for events at that facility.
      </p>

      <h2>PHI safety</h2>
      <ul>
        <li>
          When a destruction event is resident-specific (bedside wastage,
          deceased, discharge return), the resident UUID is captured for
          chain-of-custody but the audit log records only counts + DEA
          schedules — never resident name, MRN, or drug strength specifics.
        </li>
        <li>
          Witness license numbers are stored server-side under RLS but never
          appear in audit metadata or the public verification response.
        </li>
        <li>
          Signature blobs (PNG data URLs) are stored alongside the witness
          row but never emitted to audit metadata.
        </li>
      </ul>

      <h2>Limitations</h2>
      <p>
        WeConsult Rx provides a documentation tool. The pharmacist or
        facility remains responsible for compliance with DEA, state board,
        and accreditation requirements applicable to their jurisdiction.
        Specifically:
      </p>
      <ul>
        <li>
          State rules are seeded as of the date shown in the
          <code>last_updated</code> field. Verify against current state board
          publications before relying on them in production.
        </li>
        <li>
          Each seeded row carries the marker
          <code>requires-compliance-counsel-review-before-production-use</code>
          in the <code>notes</code> field as a prompt to obtain counsel
          review.
        </li>
        <li>
          DEA registration verification is the responsibility of the
          facility; we do not query DEA's online registry.
        </li>
        <li>
          State board license verification URL fields are advisory only; we
          do not call upstream verification APIs.
        </li>
        <li>
          Compliance with federal and state law is the sole responsibility
          of the registrant.
        </li>
      </ul>

      <h2>Disclaimer</h2>
      <p>
        <strong>
          This tool does not constitute legal advice. Consult with your
          compliance officer or counsel before deploying it in production
          and before relying on its output for regulatory inspections.
          Verify state-specific implementation against current statutes,
          board rules, and accreditation standards.
        </strong>
      </p>
    </LegalPage>
  );
}
